import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { fpsFilters } from "../filterConfigurations";

const Fps = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["FPS"]?.edit;
  const { data: terms, loading, error } = useFirestore("Fps");

  // Create dynamic filter configuration with Department options
  const dynamicFilters = useMemo(() => {
    const filters = [...fpsFilters];

    // Find and update the Department filter with actual data
    const departmentFilterIndex = filters.findIndex(
      (f) => f.name === "Sinclair_Department",
    );
    if (departmentFilterIndex !== -1 && terms) {
      // Extract unique departments from existing FPS data
      const uniqueDepartments = [
        ...new Set(
          terms.map((fps) => fps.Sinclair_Department).filter(Boolean), // Remove null/undefined/empty values
        ),
      ].sort();

      const departmentOptions = uniqueDepartments.map((dept) => ({
        value: dept,
        label: dept,
      }));

      filters[departmentFilterIndex] = {
        ...filters[departmentFilterIndex],
        options: departmentOptions,
      };
    }

    return filters;
  }, [terms]);

  const [selectedTerm, setSelectedTerm] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Fps";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(terms || [], dynamicFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") return "No";
    if (value === -1 || value === "-1") return "Yes";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        renderCell: (params) =>
          params?.value !== undefined
            ? getBooleanDisplayValue(params.value)
            : "",
        valueFormatter: (value) =>
          value !== undefined && value !== null
            ? getBooleanDisplayValue(value)
            : "",
      },
      { field: "Last_Name", headerName: "Last Name", flex: 1, minWidth: 120 },
      { field: "First_name", headerName: "First Name", flex: 1, minWidth: 120 },
      { field: "Full_Name", headerName: "Full Name", flex: 1, minWidth: 150 },
      { field: "Courses", headerName: "Courses", flex: 1, minWidth: 120 },
      {
        field: "Career_Days",
        headerName: "Career Days",
        flex: 1,
        minWidth: 120,
        renderCell: (params) =>
          params?.value !== undefined
            ? getBooleanDisplayValue(params.value)
            : "",
        valueFormatter: (value) =>
          value !== undefined && value !== null
            ? getBooleanDisplayValue(value)
            : "",
      },
      {
        field: "Sinclair_Department",
        headerName: "Department",
        flex: 1,
        minWidth: 150,
      },
      { field: "Title", headerName: "Title", flex: 1, minWidth: 120 },
      {
        field: "Sinclair_Location",
        headerName: "Location",
        flex: 1,
        minWidth: 120,
      },
      { field: "Sinclair_Phone", headerName: "Phone", flex: 1, minWidth: 120 },
      { field: "Sinclair_Email", headerName: "Email", flex: 1, minWidth: 150 },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((term, index) => ({
      id: term.id || index, // Use term.id if available, otherwise use index
      ...term,
    }));
  }, [filteredData]);

  const templateFields = Array.from(
    new Set([
      ...columns.map((col) => col.field),
      "Project_Manager_Name",
      "Project_Manager_Id",
      "Sinclair_Division",
      "Sinclair_Division_abv",
      "Note",
      "Tartan_Id",
    ]),
  );

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedTerm(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedTerm(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching data: {error}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={dynamicFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="FPS Filters"
          resultCount={filteredData?.length || 0}
          totalCount={terms?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="Fps"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={terms}
              templateFields={templateFields}
            />
          )}
          {canEdit && <ClearCollectionButton collectionName="Fps" />}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedTerm && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          termDetail={selectedTerm}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Fps;
